import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';

import Breadcrumb from '@/components/common/Breadcrumb';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Table from '@/components/admin/Table';
import DropdownButton, { DropdownItem } from '@/components/ui/DropdownButton';
import { mabarService, DashboardData, DashboardParticipant } from '@/services/mabarService';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

// Items per page
const ITEMS_PER_PAGE = 10;

// Format currency
const formatCurrency = (value: number) => {
  return `Rp. ${value.toLocaleString('id-ID')}`;
};

// Format date
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const MabarDashboardPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState<DashboardParticipant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Payment status change state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [participantToUpdate, setParticipantToUpdate] = useState<DashboardParticipant | null>(null);
  const [newPaymentStatus, setNewPaymentStatus] = useState<'paid' | 'unpaid'>('paid');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Load dashboard data
  const loadDashboard = async (page: number = 1) => {
    if (!slug) return;

    setLoading(true);
    try {
      const data = await mabarService.getEventDashboard(slug, page, ITEMS_PER_PAGE);
      setDashboard(data);
    } catch (error: any) {
      console.error('Failed to load dashboard:', error);
      showToast.error(error.message || 'Gagal memuat data dashboard');
      navigate('/event');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard(currentPage);
  }, [slug, currentPage]);

  // (No dropdown ref required for client table; actions handled via DropdownButton)

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (dashboard?.pagination.total_pages || 1)) {
      setCurrentPage(page);
    }
  };

  // Generate page numbers
  const getPageNumbers = (): (number | string)[] => {
    const totalPages = dashboard?.pagination.total_pages || 1;
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle Live List Member click
  const handleLiveListClick = () => {
    if (dashboard?.live_list_url) {
      void openExternalUrl(dashboard.live_list_url);
    } else if (slug) {
      navigate(`/event/${slug}`);
    }
  };

  // Handle delete participant
  const handleDeleteClick = (participant: DashboardParticipant) => {
    setParticipantToDelete(participant);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!slug || !participantToDelete) return;

    setIsDeleting(true);
    try {
      await mabarService.deleteParticipant(slug, participantToDelete.id);
      showToast.success('Participant berhasil dihapus');
      setIsDeleteModalOpen(false);
      setParticipantToDelete(null);
      // Reload data
      loadDashboard(currentPage);
    } catch (error: any) {
      showToast.error(error.message || 'Gagal menghapus participant');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle payment status change
  const handleStatusClick = (participant: DashboardParticipant, status: 'paid' | 'unpaid') => {
    setParticipantToUpdate(participant);
    setNewPaymentStatus(status);
    setIsStatusModalOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!slug || !participantToUpdate) return;

    setIsUpdatingStatus(true);
    try {
      await mabarService.updatePaymentStatus(slug, participantToUpdate.id, newPaymentStatus);
      showToast.success('Status pembayaran berhasil diubah');
      setIsStatusModalOpen(false);
      setParticipantToUpdate(null);
      // Reload data
      loadDashboard(currentPage);
    } catch (error: any) {
      showToast.error(error.message || 'Gagal mengubah status pembayaran');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Event', href: '/event' },
    { label: 'Mabar', href: '/event' },
    { label: 'Detail Mabar', href: '' },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Icon icon="mdi:alert-circle-outline" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Data tidak ditemukan</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Breadcrumb items={breadcrumbItems} />

      {/* Event Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-xl font-semibold text-gray-900">{dashboard.name}</h1>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  dashboard.visibility === 'public'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-purple-100 text-purple-600'
                }`}
              >
                {dashboard.visibility === 'public' ? 'Public' : 'Private'}
              </span>
            </div>
            <p className="text-gray-600 mb-2">{dashboard.lokasi_arena}</p>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Icon icon="mdi:soccer" className="w-4 h-4" />
                {dashboard.sport_category.name}
              </div>
              <div className="flex items-center gap-1">
                <Icon icon="mdi:calendar-outline" className="w-4 h-4" />
                {formatDate(dashboard.event_date)}
              </div>
              <div className="flex items-center gap-1">
                <Icon icon="mdi:clock-outline" className="w-4 h-4" />
                {formatLocalEventTimeRange(dashboard.start_time, dashboard.end_time)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Total Slot */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Icon icon="mdi:account-group-outline" className="w-5 h-5" />
            <span className="text-sm">Total Slot</span>
          </div>
          <p className="text-2xl font-semibold text-gray-900">
            {dashboard.stats.filled_slots}/{dashboard.stats.total_slots} (
            {dashboard.stats.slot_percentage}%)
          </p>
        </div>

        {/* Profit */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Icon icon="mdi:cash-multiple" className="w-5 h-5" />
            <span className="text-sm">Profit</span>
          </div>
          <p className="text-2xl font-semibold text-green-500">
            {formatCurrency(dashboard.stats.profit)}
          </p>
        </div>

        {/* Expenses */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Icon icon="mdi:cash-minus" className="w-5 h-5" />
            <span className="text-sm">Expenses</span>
          </div>
          <p className="text-2xl font-semibold text-gray-900">
            {formatCurrency(dashboard.stats.expenses)}
          </p>
        </div>

        {/* Income */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Icon icon="mdi:cash-plus" className="w-5 h-5" />
            <span className="text-sm">Income</span>
          </div>
          <p className="text-2xl font-semibold text-gray-900">
            {formatCurrency(dashboard.stats.income)}
          </p>
        </div>
      </div>

      {/* Member Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="md:text-lg font-semibold text-gray-900">Daftar Member</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLiveListClick}
            className="border-primary-500 text-primary-500 hover:bg-primary-50"
            roundedClass="rounded-md"
          >
            <Icon icon="mdi:account-group-outline" className="w-5 h-5 mr-2" />
            Live List Member
          </Button>
        </div>

        {/* Table */}
        {dashboard.participants.length === 0 ? (
          <div className="text-center py-12">
            <Icon icon="mdi:account-off-outline" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Belum Ada Member</h3>
            <p className="text-gray-600">Belum ada peserta yang bergabung ke event ini.</p>
          </div>
        ) : (
          <>
            <Table
              columns={[
                { key: 'name', title: 'Nama Member', width: 'w-96' },
                { key: 'wa', title: 'Whatsapp', width: 'w-80' },
                { key: 'email', title: 'Email', width: 'w-80' },
                { key: 'payment', title: 'Status Pembayaran', width: 'w-80' },
              ]}
              data={dashboard.participants.map((p) => ({
                id: p.id,
                name: p.name,
                wa: p.wa_number,
                email: p.email,
                payment: p.payment_status,
                raw: p,
              }))}
              renderCell={(row, key) => {
                if (key === 'payment') {
                  const status = row.payment as 'paid' | 'unpaid';
                  return (
                    <div className="inline-flex items-center gap-2">
                      <select
                        value={status}
                        onChange={(e) =>
                          handleStatusClick(row.raw, e.target.value === 'paid' ? 'paid' : 'unpaid')
                        }
                        className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                      >
                        <option value="paid">PAID</option>
                        <option value="unpaid">UNPAID</option>
                      </select>
                    </div>
                  );
                }
                return row[key];
              }}
              renderActions={(row) => {
                const items: DropdownItem[] = [
                  {
                    label: 'Delete',
                    icon: 'mdi:delete-outline',
                    onClick: () => handleDeleteClick(row.raw),
                    destructive: true,
                  },
                ];
                return (
                  <DropdownButton
                    items={items}
                    triggerVariant="ghost"
                    triggerSize="sm"
                    triggerClassName="px-1 py-1"
                  />
                );
              }}
            />

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(currentPage * ITEMS_PER_PAGE, dashboard.pagination.total)} of{' '}
                {dashboard.pagination.total} entries
              </p>

              <div className="flex items-center gap-1">
                <select
                  value={ITEMS_PER_PAGE}
                  className="border border-gray-300 rounded px-2 py-1 text-sm mr-4"
                  disabled
                >
                  <option value={10}>10</option>
                </select>

                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon icon="mdi:chevron-left" className="w-4 h-4" />
                </button>

                {getPageNumbers().map((page, idx) => (
                  <button
                    key={idx}
                    onClick={() => typeof page === 'number' && handlePageChange(page)}
                    disabled={page === '...'}
                    className={`min-w-[32px] h-8 px-2 rounded text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-primary-500 text-white'
                        : page === '...'
                          ? 'cursor-default'
                          : 'border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === dashboard.pagination.total_pages}
                  className="p-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon icon="mdi:chevron-right" className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Konfirmasi Hapus"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Apakah Anda yakin ingin menghapus <strong>{participantToDelete?.name}</strong> dari
            event ini?
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isDeleting && <Icon icon="mdi:loading" className="w-4 h-4 animate-spin" />}
              Ya, Hapus
            </button>
          </div>
        </div>
      </Modal>

      {/* Payment Status Change Modal */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title="Konfirmasi Ubah Status"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Apakah Anda yakin ingin mengubah status pembayaran{' '}
            <strong>{participantToUpdate?.name}</strong> menjadi{' '}
            <strong className={newPaymentStatus === 'paid' ? 'text-green-600' : 'text-orange-600'}>
              {newPaymentStatus === 'paid' ? 'PAID' : 'UNPAID'}
            </strong>
            ?
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsStatusModalOpen(false)}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirmStatusChange}
              loading={isUpdatingStatus}
            >
              Ya, Ubah
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MabarDashboardPage;
